import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Truck, Package, Code2, HelpCircle,
  ArrowLeft, CheckCircle2
} from 'lucide-react';
import { useLocalizedUrl } from '../utils/urlHelper';
import SeoMeta from '../components/SeoMeta';
import { PageBand } from '../components/common/B2B';
import ClientLogos from '../components/common/ClientLogos.jsx';
import BookingComponent from '../components/BookingComponent';
import ContactChannels from '../components/common/ContactChannels';

// Calendario público de demos (mismo que /meeting).
const BOOKING_TOKEN = '2719ff77-7a99-47a7-b9cd-8a32161ef920';

// ─── Textos ──────────────────────────────────────────────────────────────────

const WIZARD_I18N = {
  es: {
    heroDefault: 'Reserve su demostración',
    heroSubtitle: 'Elija día y hora para ver BlueCMR aplicado a su operativa. Le confirmamos la cita por correo.',
    stepProfileTitle: '¿Qué perfil describe mejor a su empresa?',
    stepProfileSubtitle: 'Dos preguntas rápidas para preparar la demostración a su medida',
    stepVolumeTitle: 'Promedio de envíos mensuales',
    stepVolumeSubtitle: '¿Cuántos envíos hace su empresa al mes, de media?',
    stepBookingTitle: 'Elija día y hora',
    stepBookingSubtitle: 'Videollamada de 45 minutos. Le enseñamos cómo se adapta BlueCMR a su operativa.',
    back: 'Volver',
    preferForm: '¿Prefiere escribirnos?',
    preferFormLink: 'Formulario de contacto',
    perMonth: 'envíos/mes',
    profileShipper: 'Cargador',
    profileShipperDesc: 'Empresas que envían mercancía',
    profileCarrier: 'Transportista / Operador',
    profileCarrierDesc: 'Empresas de transporte y logística',
    profileSoftware: 'Proveedor de software',
    profileSoftwareDesc: 'Empresas tecnológicas e integradores',
    profileOther: 'Otro',
    profileOtherDesc: 'Consultas generales',
    seoTitle: 'Reserve su demostración — BlueCMR',
    seoDescription: 'Reserve una videollamada de 45 minutos para ver BlueCMR aplicado a su operativa.',
  },
  en: {
    heroDefault: 'Book your demonstration',
    heroSubtitle: 'Pick a day and time to see BlueCMR applied to your operations. We confirm the appointment by email.',
    stepProfileTitle: 'Which profile best fits your company?',
    stepProfileSubtitle: 'Two quick questions so we can tailor the demo',
    stepVolumeTitle: 'Average monthly shipments',
    stepVolumeSubtitle: 'What is your company\'s average monthly shipment volume?',
    stepBookingTitle: 'Pick a day and time',
    stepBookingSubtitle: '45-minute video call. We show you how BlueCMR adapts to your operations.',
    back: 'Back',
    preferForm: 'Prefer to write to us?',
    preferFormLink: 'Contact form',
    perMonth: 'shipments/month',
    profileShipper: 'Shipper',
    profileShipperDesc: 'Companies that ship goods',
    profileCarrier: 'Carrier / Operator',
    profileCarrierDesc: 'Transport and logistics companies',
    profileSoftware: 'Software provider',
    profileSoftwareDesc: 'Technology companies and integrators',
    profileOther: 'Other',
    profileOtherDesc: 'General inquiries',
    seoTitle: 'Book your demonstration — BlueCMR',
    seoDescription: 'Book a 45-minute video call to see BlueCMR applied to your operations.',
  },
};

// ─── Orígenes (titular según la página de la que se viene) ───────────────────

const ORIGINS = {
  'dca-2026': {
    title: 'Documento de control electrónico (DeCA)',
    subtitle: 'Le enseñamos cómo emitirlo y firmarlo desde su operativa actual',
  },
  'ecmr': {
    title: 'eCMR: el CMR en digital',
    subtitle: 'Le enseñamos cómo funciona en sus envíos internacionales',
  },
  'transportistas': {
    title: 'BlueCMR para transportistas y flotas',
    subtitle: 'Le enseñamos cómo funciona con sus servicios y sus conductores',
  },
  'cargadores': {
    title: 'BlueCMR para cargadores',
    subtitle: 'Le enseñamos cómo funciona con sus expediciones',
  },
  'integracion': {
    title: 'Integración con su software',
    subtitle: 'Cómo conectar BlueCMR con su TMS, ERP o plataforma logística',
  },
  'precios': {
    title: 'Consulta personalizada de precios',
    subtitle: 'Le preparamos una propuesta según su volumen',
  },
  'demo': {
    title: 'Reserve su demostración',
    subtitle: 'Le enseñamos cómo se adapta BlueCMR a su operativa',
  },
};

const PROFILES = [
  { id: 'shipper', icon: Package },
  { id: 'carrier', icon: Truck },
  { id: 'software', icon: Code2 },
  { id: 'other', icon: HelpCircle },
];

const VOLUME_RANGES = [
  { id: 'under-100', label: '< 100' },
  { id: '100-500', label: '100 – 500' },
  { id: '500-2500', label: '500 – 2.500' },
  { id: '2500-10000', label: '2.500 – 10.000' },
  { id: 'over-10000', label: '+ 10.000' },
];

// ─── Componentes auxiliares ──────────────────────────────────────────────────

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`h-1 transition-all duration-300 ${
          i === current ? 'w-10 bg-primary-700' : i < current ? 'w-6 bg-slate-400' : 'w-6 bg-slate-200'
        }`}
      />
    ))}
  </div>
);

const SelectableCard = ({ icon: Icon, label, description, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      w-full px-6 py-5 border text-left transition-colors duration-200
      flex items-center gap-5 min-h-[88px] cursor-pointer bg-white
      ${selected
        ? 'border-primary-700 shadow-[inset_4px_0_0_0_#1d4ed8]'
        : 'border-slate-300 hover:border-primary-700'
      }
    `}
  >
    <Icon className="w-7 h-7 flex-shrink-0 text-primary-700" strokeWidth={1.5} />
    <div className="flex-1 min-w-0">
      <p className={`font-semibold text-lg ${selected ? 'text-primary-700' : 'text-slate-900'}`}>
        {label}
      </p>
      {description && (
        <p className="text-sm mt-1 text-slate-500">
          {description}
        </p>
      )}
    </div>
    {selected && (
      <CheckCircle2 className="w-6 h-6 text-primary-700 flex-shrink-0" />
    )}
  </button>
);

// ─── Componente principal ────────────────────────────────────────────────────

/**
 * Reserva de demo con cualificación previa: perfil → volumen (cargadores y
 * transportistas) → calendario. Perfil y volumen van en el asunto de la
 * reserva para que el equipo prepare la demo.
 */
const ContactWizardPage = () => {
  const { i18n } = useTranslation();
  const { getLocalizedUrl } = useLocalizedUrl();
  const [searchParams] = useSearchParams();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const w = WIZARD_I18N[lang];

  // Titular según origen: ?origin=dca-2026 o, como respaldo, ?title=...
  const originKey = searchParams.get('origin') || '';
  const originConfig = (lang === 'es' && ORIGINS[originKey]) || null;
  const customTitle = originConfig?.title || searchParams.get('title') || null;
  const customSubtitle = originConfig?.subtitle || null;
  const utmSource = searchParams.get('utm_source') || originKey || '';

  // Precualificación: ?profile=shipper|carrier|software|other salta el paso 1
  const presetProfile = searchParams.get('profile') || '';

  const [step, setStep] = useState(() => {
    if (presetProfile === 'shipper' || presetProfile === 'carrier') return 1; // volumen
    if (presetProfile === 'software' || presetProfile === 'other') return 2; // calendario
    return 0;
  });
  const [profile, setProfile] = useState(presetProfile || null);
  const [volume, setVolume] = useState(null);

  // ─── Navegación ────────────────────────────────────────────────────────────

  const needsVolume = profile === 'shipper' || profile === 'carrier';
  const totalSteps = needsVolume ? 3 : 2;

  const handleProfileSelect = (id) => {
    setProfile(id);
    setTimeout(() => {
      if (id === 'shipper' || id === 'carrier') {
        setStep(1);
      } else {
        setVolume(null);
        setStep(2);
      }
    }, 200);
  };

  const handleVolumeSelect = (id) => {
    setVolume(id);
    setTimeout(() => setStep(2), 200);
  };

  const goBack = () => {
    if (step === 2 && needsVolume) {
      setStep(1);
    } else if (step === 2) {
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };

  // ─── Reserva ───────────────────────────────────────────────────────────────

  const getProfileLabel = (id) => {
    const labels = { shipper: w.profileShipper, carrier: w.profileCarrier, software: w.profileSoftware, other: w.profileOther };
    return labels[id] || id;
  };
  const getProfileDesc = (id) => {
    const descs = { shipper: w.profileShipperDesc, carrier: w.profileCarrierDesc, software: w.profileSoftwareDesc, other: w.profileOtherDesc };
    return descs[id] || '';
  };

  const volumeLabel = volume ? VOLUME_RANGES.find(r => r.id === volume)?.label : null;
  const bookingSubject = [
    `Demo: ${getProfileLabel(profile)}`,
    volumeLabel ? `${volumeLabel} ${w.perMonth}` : null,
    utmSource ? `origen ${utmSource}` : null,
  ].filter(Boolean).join(' / ');

  const handleBookingConfirmed = () => {
    if (window.$zoho && window.$zoho.salesiq) {
      window.$zoho.salesiq.visitor.customaction(JSON.stringify({
        action: 'demo_wizard_booked',
        profile,
        volume: volume || 'n/a',
      }));
    }
    // Conversión de Google Ads (misma que el formulario de contacto)
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-11012055024/4B25CKWA9qIaEPC_-oIp',
        'value': 1.0,
        'currency': 'EUR',
      });
    }
  };

  // ─── Pasos ─────────────────────────────────────────────────────────────────

  const renderStepProfile = () => (
    <div className="animate-fade-in">
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 text-center mb-3">
        {w.stepProfileTitle}
      </h2>
      <p className="text-slate-600 text-center mb-8">
        {w.stepProfileSubtitle}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
        {PROFILES.map((p) => (
          <SelectableCard
            key={p.id}
            icon={p.icon}
            label={getProfileLabel(p.id)}
            description={getProfileDesc(p.id)}
            selected={profile === p.id}
            onClick={() => handleProfileSelect(p.id)}
          />
        ))}
      </div>
    </div>
  );

  const renderStepVolume = () => (
    <div className="animate-fade-in">
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 text-center mb-3">
        {w.stepVolumeTitle}
      </h2>
      <p className="text-slate-600 text-center mb-8">
        {w.stepVolumeSubtitle}
      </p>
      <div className="flex flex-col gap-3 max-w-md mx-auto">
        {VOLUME_RANGES.map((r) => (
          <SelectableCard
            key={r.id}
            icon={Package}
            label={r.label}
            description={w.perMonth}
            selected={volume === r.id}
            onClick={() => handleVolumeSelect(r.id)}
          />
        ))}
      </div>
    </div>
  );

  const renderStepBooking = () => (
    <div className="animate-fade-in">
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 text-center mb-3">
        {w.stepBookingTitle}
      </h2>
      <p className="text-slate-600 text-center mb-8">
        {w.stepBookingSubtitle}
      </p>
      <div className="flex justify-center">
        <BookingComponent
          bookingToken={BOOKING_TOKEN}
          locale={lang}
          prefillSubject={bookingSubject}
          onBookingConfirmed={handleBookingConfirmed}
        />
      </div>
      <p className="text-center text-sm text-gray-500 mt-8">
        {w.preferForm}{' '}
        <Link to={getLocalizedUrl('/contacto/formulario')} className="text-primary-700 font-semibold hover:underline">
          {w.preferFormLink}
        </Link>
      </p>
      <ContactChannels lang={lang} compact className="mt-2" />
    </div>
  );

  // ─── Render principal ──────────────────────────────────────────────────────

  const currentStepForIndicator = (() => {
    if (step === 0) return 0;
    if (step === 1 && needsVolume) return 1;
    return totalSteps - 1;
  })();

  return (
    <div className="min-h-screen bg-white">
      <SeoMeta
        title={w.seoTitle}
        description={w.seoDescription}
        canonical="https://bluecmr.com/contacto/demo"
        alternates={{
          es: 'https://www.bluecmr.com/contacto/demo',
          en: 'https://www.bluecmr.com/en/contact/demo',
          'x-default': 'https://www.bluecmr.com/contacto/demo'
        }}
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: 'Contacto', url: 'https://bluecmr.com/contacto' },
          { name: 'Reserve su demostración', url: 'https://bluecmr.com/contacto/demo' },
        ]}
      />

      {/* Hero */}
      <PageBand eyebrow={lang === 'es' ? 'Contacto' : 'Contact'} title={customTitle || w.heroDefault} text={customSubtitle || w.heroSubtitle} />

      {/* Pasos */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <StepIndicator current={currentStepForIndicator} total={totalSteps} />

          <Link
            to={step > 0 ? '#' : getLocalizedUrl('/contacto')}
            onClick={step > 0 ? (e) => { e.preventDefault(); goBack(); } : undefined}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {w.back}
          </Link>

          {step === 0 && renderStepProfile()}
          {step === 1 && needsVolume && renderStepVolume()}
          {(step === 2 || (step === 1 && !needsVolume)) && renderStepBooking()}

          {/* Prueba social antes de reservar. Logos siempre en B/N y pequeños (condición de su permiso) */}
          <ClientLogos title={lang === 'es' ? 'Algunas de las empresas que trabajan con BlueCMR' : 'Some of the companies working with BlueCMR'} embedded />
        </div>
      </section>
    </div>
  );
};

export default ContactWizardPage;
